<template>
  <div class="comments-panel">
    <!-- Header -->
    <div class="comments-header mb-3">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h6 class="mb-0">
          Proof Comments
        </h6>
        <small
          v-if="proofInfo"
          class="text-muted"
        >
          <i class="fa fa-info-circle" />
          {{ proofInfo.version_name || 'Latest' }}
        </small>
      </div>

      <div
        v-if="proofInfo"
        class="alert alert-info small py-2 mb-2"
      >
        <i class="fa fa-share-alt" />
        Comments from shared proof page
      </div>

      <div
        v-if="!hasProof"
        class="alert alert-warning small py-2"
      >
        <i class="fa fa-exclamation-triangle" />
        No proof generated yet. Create a proof to enable comments.
        <button
          type="button"
          class="btn btn-link btn-sm d-block p-0 mt-1"
          @click="openCreateProofModal"
        >
          Create proof
        </button>
      </div>
    </div>

    <!-- Filter -->
    <div
      v-if="hasProof"
      class="comments-filter btn-group btn-group-sm w-100 mb-3"
    >
      <button
        class="btn"
        :class="!showResolved ? 'btn-primary' : 'btn-outline-primary'"
        @click="showResolved = false"
      >
        Open Only ({{ openCount }})
      </button>
      <button
        class="btn"
        :class="showResolved ? 'btn-primary' : 'btn-outline-primary'"
        @click="showResolved = true"
      >
        All ({{ totalCount }})
      </button>
    </div>

    <!-- Comments List -->
    <div
      v-if="hasProof"
      class="comments-list"
    >
      <div
        v-if="loading"
        class="text-center p-3"
      >
        <i class="fa fa-spinner fa-spin" /> Loading comments...
      </div>

      <div
        v-else-if="filteredComments.length === 0"
        class="text-center text-muted p-3"
      >
        <i
          class="fa fa-comments-o fa-2x mb-2"
        />
        <p class="mb-0">
          {{ showResolved ? 'No comments yet' : 'No open comments' }}
        </p>
      </div>

      <CommentItem
        v-for="comment in filteredComments"
        :key="comment.id + '-' + selectedCommentId"
        :data-comment-id="comment.id"
        :comment="comment"
        :show-proof-link="true"
        :proof-share-link="proofShareLink"
        :is-highlighted="isCommentHighlighted(comment.id)"
        @reply="handleReply"
        @resolve="handleResolve"
        @resolved="handleCommentResolved"
        @approved="handleCommentApproved"
        @delete="handleDelete"
        @deleted="handleCommentDeleted"
        @edit="handleEdit"
        @click.native="handleCommentClick(comment)"
      />
    </div>

    <!-- Add Comment -->
    <div
      v-if="hasProof"
      class="add-comment-section mt-3 pt-3 border-top"
    >
      <button
        v-if="!showCommentInput"
        class="btn btn-sm btn-primary w-100"
        @click="showCommentInput = true"
      >
        <i class="fa fa-plus" /> Add Comment
      </button>

      <div
        v-if="showCommentInput"
        class="simple-comment-input"
      >
        <div
          v-if="editingCommentId"
          class="alert alert-warning py-2 px-3 mb-2 d-flex justify-content-between align-items-center"
        >
          <small>
            <i class="fa fa-pencil" /> Editing comment
          </small>
          <button
            type="button"
            class="close"
            @click="editingCommentId = null; newCommentText = ''"
          >
            <span>&times;</span>
          </button>
        </div>
        <div
          v-else-if="replyToCommentId"
          class="alert alert-info py-2 px-3 mb-2 d-flex justify-content-between align-items-center"
        >
          <small>
            <i class="fa fa-reply" /> Replying to {{ replyToCommentUsername }}
          </small>
          <button
            type="button"
            class="close"
            @click="replyToCommentId = null"
          >
            <span>&times;</span>
          </button>
        </div>
        <b-form-textarea
          v-model="newCommentText"
          placeholder="Write a comment..."
          rows="3"
          class="mb-2"
          :disabled="submitting"
        />
        <div class="d-flex justify-content-end">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-2"
            :disabled="submitting"
            @click="closeCommentInput"
          >
            Cancel
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :disabled="!newCommentText.trim() || submitting"
            @click="submitComment"
          >
            <i
              v-if="!submitting"
              class="fa fa-check"
            />
            <i
              v-else
              class="fa fa-spinner fa-spin"
            />
            <template v-if="editingCommentId">
              {{ submitting ? 'Updating...' : 'Update' }}
            </template>
            <template v-else>
              {{ submitting ? 'Submitting...' : 'Submit' }}
            </template>
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommentItem from '@frontend/components/common/share/CommentItem.vue'
import axios from 'axios'

export default {
  name: 'CommentsPanel',
  components: {
    CommentItem
  },
  props: {
    layoutId: {
      type: [Number, String],
      required: true
    },
    isTabActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      proofInfo: null,
      proofId: null,
      comments: [],
      showResolved: false,
      showCommentInput: false,
      newCommentText: '',
      replyToCommentId: null,
      editingCommentId: null,
      proofShareLink: null,
      selectedCommentId: null
    }
  },
  computed: {
    hasProof() {
      return this.proofInfo !== null && this.proofId !== null
    },
    selectedInstance() {
      return this.$store.getters['layout/selectedInstance']
    },
    filteredComments() {
      if (!Array.isArray(this.comments)) return []

      return this.comments.filter(comment => {
        // Only show top-level comments
        if (comment.parent_id) return false

        // In "Open Only" mode, hide resolved and approved comments
        if (!this.showResolved) {
          if (comment.resolved) return false
          if (comment.status === 'approved') return false
        }

        return true
      })
    },
    totalCount() {
      if (!Array.isArray(this.comments)) return 0
      return this.comments.filter(c => !c.parent_id).length
    },
    openCount() {
      if (!Array.isArray(this.comments)) return 0
      return this.comments.filter(c => !c.parent_id && !c.resolved && c.status !== 'approved').length
    },
    replyToCommentUsername() {
      if (!this.replyToCommentId) return ''

      // Find the comment in the flat list or nested replies
      const findComment = (comments) => {
        for (const comment of comments) {
          if (comment.id === this.replyToCommentId) {
            return comment.user_name || 'User'
          }
          if (comment.replies_recursive && comment.replies_recursive.length) {
            const found = findComment(comment.replies_recursive)
            if (found) return found
          }
        }
        return null
      }

      return findComment(this.comments) || 'User'
    }
  },
  watch: {
    isTabActive(newVal) {
      if (newVal && !this.proofInfo) {
        this.loadProofInfo()
      }
      // Hide pins and clear template highlight when leaving the comments tab
      if (!newVal) {
        this.clearPinsFromTemplates()
        this.selectedCommentId = null
        if (window.clearTemplateHighlight) {
          window.clearTemplateHighlight()
        }
      } else if (newVal && this.comments.length > 0) {
        // Re-render pins when coming back to comments tab
        this.renderPinsOnTemplates()
      }
    },
    layoutId() {
      // Reload if layout changes
      this.proofInfo = null
      this.comments = []
      if (this.isTabActive) {
        this.loadProofInfo()
      }
    }
  },
  mounted() {
    if (this.isTabActive) {
      this.loadProofInfo()
    }
    this.setupEchoListener()
    // Listen for highlight cleared events from show.js
    window.addEventListener('template-highlight-cleared', this.handleHighlightCleared)
    // Listen for pin clicks from show.js
    window.addEventListener('pin-clicked', this.handlePinClicked)
  },
  beforeDestroy() {
    this.teardownEchoListener()
    this.clearPinsFromTemplates()
    window.removeEventListener('template-highlight-cleared', this.handleHighlightCleared)
    window.removeEventListener('pin-clicked', this.handlePinClicked)
  },
  methods: {
    openCreateProofModal() {
      $('#shareProofModal').modal('show')
    },

    async loadProofInfo() {
      try {
        this.loading = true
        const response = await axios.get(`/banner/groups/${this.layoutId}/get_latest_share_proof_link_for_version`)

        if (response.data && response.data.url) {
          this.proofInfo = {
            id: response.data.shared_proof_banner_id,
            version_name: response.data.version_name || 'Latest',
            url: response.data.url
          }
          this.proofId = response.data.shared_proof_banner_id
          // Extract the hash from the URL (e.g., from "https://app.rapidads.io/banner/group/share/abc123" get "abc123")
          const urlParts = response.data.url.split('/share/')
          this.proofShareLink = urlParts.length > 1 ? urlParts[1].split('?')[0] : response.data.link_hash

          await this.loadComments()
        } else {
          this.proofInfo = null
          this.proofId = null
        }
      } catch (error) {
        console.error('Error loading proof info:', error)
        this.proofInfo = null
      } finally {
        this.loading = false
      }
    },

    async loadComments() {
      if (!this.proofId) return

      try {
        this.loading = true
        const response = await axios.post('/history_projects/get_comment', {
          comment_type: 3, // shared proof
          type_id: this.proofId
        })

        // Handle different response structures
        let commentsData = response.data

        // If response.data is an object with a 'comment', 'data', or 'comments' property, use that
        if (commentsData && typeof commentsData === 'object' && !Array.isArray(commentsData)) {
          commentsData = commentsData.comment || commentsData.data || commentsData.comments || []
        }

        // Ensure it's an array
        this.comments = Array.isArray(commentsData) ? commentsData : []
        this.$emit('comments-updated', this.openCount)

        // Render pins on templates
        this.renderPinsOnTemplates()
      } catch (error) {
        console.error('Error loading comments:', error)
        this.comments = []
      } finally {
        this.loading = false
      }
    },

    async submitComment() {
      if (!this.newCommentText.trim() || this.submitting) return

      this.submitting = true

      try {
        if (this.editingCommentId) {
          // Update existing comment
          await axios.post(`/history_projects/comment/${this.editingCommentId}`, {
            comment: this.newCommentText
          })

          if (this.$toasted) {
            this.$toasted.success('Comment updated successfully')
          }
        } else {
          // Create new comment (general comment, not associated with any specific template)
          const commentData = {
            comment_type: 3,
            type_id: this.proofId,
            comment: this.newCommentText,
            parent_id: this.replyToCommentId
          }

          await axios.post('/history_projects/comment', commentData)

          if (window.toastr) {
            window.toastr.success('Comment added successfully')
          }
        }

        // Reload comments
        await this.loadComments()

        // Close input and clear text
        this.closeCommentInput()
      } catch (error) {
        console.error('Error with comment:', error)
        console.error('Error details:', error.response?.data)

        if (this.$toasted) {
          this.$toasted.error(this.editingCommentId ? 'Failed to update comment' : 'Failed to add comment')
        }
      } finally {
        this.submitting = false
      }
    },

    handleReply(commentId) {
      this.replyToCommentId = commentId
      this.showCommentInput = true
    },

    async handleResolve(commentId) {
      try {
        await axios.post('/history_projects/resolve_comment', {
          comment_id: commentId
        })

        await this.loadComments()
        this.$toasted.success('Comment resolved')
      } catch (error) {
        console.error('Error resolving comment:', error)
        this.$toasted.error('Failed to resolve comment')
      }
    },

    async handleDelete(commentId) {
      if (!confirm('Are you sure you want to delete this comment?')) return

      try {
        await axios.post('/history_projects/delete_comment', {
          comment_id: commentId
        })

        await this.loadComments()
        this.$toasted.success('Comment deleted')
      } catch (error) {
        console.error('Error deleting comment:', error)
        this.$toasted.error('Failed to delete comment')
      }
    },

    async handleCommentDeleted(commentId) {
      await this.loadComments()
      if (this.$toasted) {
        this.$toasted.success('Comment deleted successfully')
      }
    },

    async handleCommentResolved(commentId) {
      await this.loadComments()
      if (this.$toasted) {
        this.$toasted.success('Comment marked as resolved')
      }
    },

    async handleCommentApproved(commentId) {
      await this.loadComments()
      if (this.$toasted) {
        this.$toasted.success('Comment approved')
      }
    },

    async handleEdit(comment) {
      // Load comment text into textarea
      this.newCommentText = comment.comment.replace(/<[^>]*>/g, '') // Strip HTML tags
      this.editingCommentId = comment.id
      this.replyToCommentId = null // Clear reply mode if active
      this.showCommentInput = true
    },

    closeCommentInput() {
      this.showCommentInput = false
      this.newCommentText = ''
      this.replyToCommentId = null
      this.editingCommentId = null
    },

    isCommentHighlighted(commentId) {
      return this.selectedCommentId === commentId
    },

    handleCommentClick(comment) {
      const instanceId = comment.options?.pinData?.[0]?.instanceId

      // Check if comment has no associated template (no pin)
      if (!instanceId) {
        // Toggle selection for non-pinned comment
        if (this.selectedCommentId === comment.id) {
          // Clicking same non-pinned comment - deselect
          this.selectedCommentId = null
        } else {
          // Select this non-pinned comment and clear any template highlight
          // Use silent=true to prevent the event from resetting selectedCommentId
          if (window.clearTemplateHighlight) {
            window.clearTemplateHighlight(true)
          }
          this.selectedCommentId = comment.id
          // Show toast message
          if (window.toastr) {
            window.toastr.info('This comment is not associated with a specific template')
          }
        }
        return
      }

      // Toggle selection - if clicking same comment, it will be deselected by show.js
      if (this.selectedCommentId === comment.id) {
        this.selectedCommentId = null
      } else {
        this.selectedCommentId = comment.id
      }

      // Dispatch event to show.js for template highlighting
      window.dispatchEvent(new CustomEvent('highlight-template', {
        detail: {
          instanceId,
          commentId: comment.id
        }
      }))

      // Highlight the associated pin
      if (this.selectedCommentId) {
        window.dispatchEvent(new CustomEvent('highlight-comment-pin', {
          detail: { commentId: comment.id }
        }))
      }
    },

    handleHighlightCleared() {
      // Called when highlight is cleared from show.js (e.g., clicking same comment again)
      this.selectedCommentId = null
    },

    handlePinClicked(e) {
      // Called when a pin is clicked in show.js
      // Only select the comment and scroll to it - no template highlight needed
      // since user already clicked on the pin (they're already looking at the template)
      const { commentId } = e.detail
      const comment = this.comments.find(c => c.id === commentId)
      if (comment) {
        // Toggle selection
        if (this.selectedCommentId === commentId) {
          this.selectedCommentId = null
        } else {
          this.selectedCommentId = commentId
        }

        // Scroll to comment in the panel
        this.$nextTick(() => {
          const commentElement = this.$el.querySelector(`[data-comment-id="${commentId}"]`)
          if (commentElement) {
            commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
    },

    renderPinsOnTemplates() {
      // Send comments to show.js to render pins on templates
      window.dispatchEvent(new CustomEvent('render-comment-pins', {
        detail: { comments: this.comments }
      }))
    },

    clearPinsFromTemplates() {
      // Tell show.js to clear all pins
      window.dispatchEvent(new CustomEvent('clear-comment-pins'))
    },

    setupEchoListener() {
      if (!window.Echo || !this.proofId) return

      try {
        this.echoChannel = window.Echo.channel(`proof.${this.proofId}`)

        if (this.echoChannel) {
          this.echoChannel
            .listen('CommentAdded', () => {
              this.loadComments()
              this.showNewCommentNotification()
            })
            .listen('CommentResolved', () => {
              this.loadComments()
            })
            .listen('CommentDeleted', () => {
              this.loadComments()
            })
        }
      } catch (error) {
        console.warn('Echo not available for real-time updates:', error)
      }
    },

    teardownEchoListener() {
      if (this.echoChannel) {
        try {
          this.echoChannel.stopListening('CommentAdded')
          this.echoChannel.stopListening('CommentResolved')
          this.echoChannel.stopListening('CommentDeleted')
          window.Echo.leave(`proof.${this.proofId}`)
        } catch (error) {
          console.warn('Error tearing down Echo listener:', error)
        }
      }
    },

    showNewCommentNotification() {
      this.$toasted.info('New comment added', {
        duration: 3000,
        action: {
          text: 'View',
          onClick: (e, toastObject) => {
            // Scroll to top of comments
            const commentsList = this.$el.querySelector('.comments-list')
            if (commentsList) {
              commentsList.scrollTop = 0
            }
            toastObject.goAway(0)
          }
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.comments-panel {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 250px);
  overflow: hidden;
}

.comments-header {
  flex-shrink: 0;
}

.comments-list {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;

    &:hover {
      background: #555;
    }
  }

}

.add-comment-section {
  flex-shrink: 0;
  margin-top: auto;
}

.comments-filter {
  flex-shrink: 0;

  .btn {
    font-size: 0.875rem;
  }
}
</style>
